import { useState, FormEvent, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import ModalComponent from "components/modal";
import { useSharedState } from "state";
import { createTask, uploadTaskFiles } from "api/task";
import { getUserByManager } from "api/user";
import { User } from "types/user";

type FormErrors = Record<string, boolean>;
type FormRecords = Record<string, string>;

const REQUIRED_FIELDS = [
  "task_name",
  "priority",
  "assigned_to",
  "deadline",
  "department",
  "project",
  "description",
] as const;

const PRIORITY_TYPES = [
  {
    value: "Низкая",
    label: "Низкая",
  },
  {
    value: "Средняя",
    label: "Средняя",
  },
  {
    value: "Высокая",
    label: "Высокая",
  },
];

const getInputErrorMessage = (fieldId: string) =>
  fieldId === "file" ? "Добавьте файл" : "Введите корректное значение";

export default function NewTask() {
  const { getTasksData, notify } = useSharedState();
  const [searchParams] = useSearchParams();
  const taskType = searchParams.get("type");

  const [myUsers, setMyUsers] = useState<User[]>([]);
  const [formRecords, setFormRecords] = useState<FormRecords>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [fileList, setFileList] = useState<FileList | null>(null);
  const [isFormLoading, setFormLoading] = useState(false);
  const [isFormSubmitted, setFormSubmitted] = useState(false);

  const taskFields = [
    {
      id: "task_name",
      title: "Наименование",
      type: "text",
      placeholder: "Наименование",
      options: undefined,
    },
    {
      id: "priority",
      title: "Приоритетность",
      type: "select",
      placeholder: undefined,
      options: PRIORITY_TYPES,
    },
    {
      id: "assigned_to",
      title: "Ответственный",
      type: "select",
      placeholder: undefined,
      options: [
        ...myUsers.filter((user) => user.status).map((user) => ({ value: String(user.user_id), label: user.name })),
      ],
    },
    {
      id: "deadline",
      title: "Срок",
      type: "date",
      placeholder: "Срок",
      options: undefined,
    },
    {
      id: "department",
      title: "Отдел",
      type: "text",
      placeholder: "Отдел",
      options: undefined,
    },
    {
      id: "project",
      title: "Проект",
      type: "text",
      placeholder: "Проект",
      options: undefined,
    },
    {
      id: "description",
      title: "Описание",
      type: "textarea",
      placeholder: "Описание",
      options: undefined,
    },
    {
      id: "file",
      title: "Прикрепить файл",
      type: "file",
      placeholder: "",
      options: undefined,
    },
  ] as const;

  useEffect(() => {
    getUserByManagerData();
  }, []);

  const getUserByManagerData = async () => {
    try {
      const res: User[] = await getUserByManager();
      setMyUsers(res);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при получении пользователей");
    }
  };

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};
    REQUIRED_FIELDS.forEach((field) => {
      if (!formRecords[field]?.trim()) {
        newErrors[field] = true;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formRecords]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      const files = (e.target as HTMLInputElement).files;

      files && setFileList(files);

      setFormRecords((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    },
    []
  );

  const handleSelectChange = useCallback((option: any, actionMeta: any) => {
    const { name } = actionMeta;
    const value = option?.value || "";

    setFormRecords((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  }, []);

  const submitTask = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      const formData = new FormData();

      const paymentForm = {
        task_name: formRecords.task_name,
        deadline: formRecords.deadline,
        priority: formRecords.priority,
        description: formRecords.description,
        department: formRecords.department,
        project: formRecords.project,
        assigned_to: +formRecords.assigned_to,
      };

      const request_id = await createTask(paymentForm);

      if (fileList) {
        Array.from(fileList).forEach((file) => {
          formData.append(`files`, file);
        });

        await uploadTaskFiles(request_id, formData);
      }

      notify("Задача создана", "success");
      setFormSubmitted(true);
      getTasksData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при создании задачи");
    } finally {
      setFormLoading(false);
    }
  };

  const renderField = ({ id, title, type, placeholder, options }: (typeof taskFields)[number]) => {
    const Component = type === "textarea" ? "textarea" : "input";
    return (
      <div className={id === "description" || id === "file" ? "col-lg-12" : "col-lg-4"} key={id}>
        <div className="mb-3">
          <label className={`form-label ${id !== "file" ? "required" : ""}`}>{title}</label>
          <div className="input-icon mb-3">
            {type === "select" ? (
              <Select
                classNamePrefix="react-select"
                name={id}
                placeholder={placeholder}
                value={options?.find((opt) => opt.value === formRecords[id])}
                onChange={handleSelectChange}
                options={options}
                isDisabled={isFormLoading}
                className={errors[id] ? "is-invalid" : ""}
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            ) : (
              <Component
                type={type}
                className={`form-control ${errors[id] ? "is-invalid" : ""}`}
                name={id}
                max={type === "date" ? "9999-12-31" : undefined}
                placeholder={placeholder}
                value={formRecords[id] || ""}
                onChange={handleInputChange}
                disabled={isFormLoading}
                multiple={type === "file"}
              />
            )}
            {errors[id] && <div className="invalid-feedback">{getInputErrorMessage(id)}</div>}
            {isFormLoading && (
              <span className="input-icon-addon">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const newTaskBody = useMemo(
    () => (
      <form id="taskForm" className="row" onSubmit={submitTask}>
        {taskFields.map(renderField)}
      </form>
    ),
    [renderField, submitTask]
  );

  const newTaskFooter = useMemo(
    () => (
      <button type="submit" form="taskForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
        Создать задачу
        {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
      </button>
    ),
    [isFormLoading]
  );

  return (
    <ModalComponent
      header="Новая задача"
      body={newTaskBody}
      footer={newTaskFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
